import { useState, useEffect, useRef, useCallback } from "react";
import { imagesAPI, downloadWithFingerprint } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Download, Loader2, Lock, Sparkles } from "lucide-react";
import { AgencyLimitModal } from "@/components/dashboard/AgencyLimitModal";
import { trackMetaEvent } from "@/lib/meta";
import { trackInternalEvent } from "@/lib/analytics";

const WATERMARK_TEXT = "FoodPhotos.in  \u2022  Studio Pass 2.0";

export default function PreviewModal({ image, images, onClose, onNavigate, user, onUserUpdate, onShowPricing, showPremiumLock, isAnonymous = false, onAnonymousDownload }) {
  const canvasRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Close preview and show pricing modal
  const showPricingAndClose = () => {
    onClose();
    onShowPricing();
  };

  const previewUrl = image.secure_url.replace(
    "/upload/",
    "/upload/c_limit,w_1200,q_auto,f_auto/"
  );

  const currentIndex = images.findIndex((i) => i.public_id === image.public_id);

  // Download counter
  const downloadsUsed = user?.downloads_used ?? 0;
  const freeLimit = user?.free_download_limit ?? 2;
  const remaining = Math.max(0, freeLimit - downloadsUsed);
  const isFree = user?.plan !== "paid";
  const isStarter = user?.plan_id === "starter";
  const isPremiumLocked = (showPremiumLock ?? (isFree || isStarter)) && image.is_premium;

  const drawWatermark = useCallback((img) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    ctx.drawImage(img, 0, 0);

    ctx.save();
    const fontSize = Math.max(14, Math.min(canvas.width / 30, 22));
    ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
    ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    const metrics = ctx.measureText(WATERMARK_TEXT);
    const textWidth = metrics.width;
    const spacingX = textWidth + 60;
    const spacingY = fontSize * 6;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-30 * Math.PI / 180);

    const diagonal = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
    const startX = -diagonal;
    const startY = -diagonal;
    const endX = diagonal;
    const endY = diagonal;

    for (let y = startY; y < endY; y += spacingY) {
      for (let x = startX; x < endX; x += spacingX) {
        ctx.fillText(WATERMARK_TEXT, x, y);
      }
    }

    ctx.restore();
    setImgLoaded(true);
  }, []);

  useEffect(() => {
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => drawWatermark(img);
    img.onerror = () => {
      setImgLoaded(true);
      toast.error("Failed to load preview image");
    };
    img.src = previewUrl;
  }, [previewUrl, drawWatermark]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) onNavigate(images[currentIndex - 1]);
      if (e.key === "ArrowRight" && currentIndex < images.length - 1) onNavigate(images[currentIndex + 1]);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [currentIndex, images, onClose, onNavigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const [showAgencyLimit, setShowAgencyLimit] = useState(false);
  const [agencyDaysReset, setAgencyDaysReset] = useState(0);

  const handleDownload = async () => {
    // Anonymous user: route to OTP gate instead of hitting download API
    if (isAnonymous) {
      onAnonymousDownload?.(image);
      return;
    }

    // Premium image gating: free users see pricing modal instead
    if (isPremiumLocked) {
      trackInternalEvent("UpgradePopupClicked", {
        user_id: user?.user_id,
        metadata: { source: "download_button_premium_gate", image_id: image?.public_id },
      });
      onShowPricing();
      return;
    }

    setDownloading(true);
    trackInternalEvent("FreeDownloadStarted", {
      user_id: user?.user_id,
      product_id: image?.public_id,
      metadata: {
        dish_name: image?.dish_name || "",
        is_free_user: user?.plan !== "paid",
        downloads_used_before: user?.downloads_used ?? 0,
      },
    });
    try {
      // Use fingerprint-enabled download, with preview_opened = true (user saw preview)
      const res = await downloadWithFingerprint(image.public_id, true);
      const data = res.data;

      // Monthly fair-usage limit reached (paid users)
      if (data.code === "MONTHLY_DOWNLOAD_LIMIT_REACHED") {
        setAgencyDaysReset(data.days_until_reset || 30);
        setShowAgencyLimit(true);
        return;
      }

      // Check if download was blocked
      if (data.upgrade_required) {
        showPricingAndClose();
        return;
      }

      const { download_url, filename } = data;
      const link = document.createElement("a");
      link.href = download_url;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Dynamic toast based on download count (user.downloads_used is BEFORE this download)
      const downloadNum = (user?.downloads_used ?? 0) + 1;
      const isFreeUser = user?.plan !== "paid";

      if (isFreeUser && downloadNum === 1) {
        toast.success(
          "Sample downloaded successfully\nUse it on your menu, Zomato or Swiggy listing in minutes.",
          { duration: 4000 }
        );
      } else if (isFreeUser && downloadNum === 2) {
        toast.success(
          "Last free sample downloaded\nYou\u2019ve now seen how Studio Pass 2.0 works.",
          { duration: 4000 }
        );
      } else {
        toast.success(
          `Downloaded successfully.\nUse on: Zomato \u2022 Swiggy \u2022 Menu cards \u2022 Social media`,
          { duration: 3000 }
        );
      }

      // Fire download tracking event if backend returned one
      if (data.meta_event_id && data.meta_event_name) {
        trackMetaEvent(data.meta_event_name, data.meta_event_id);
      }
      // Internal analytics: FreeDownloadCompleted (independent of Meta)
      trackInternalEvent("FreeDownloadCompleted", {
        user_id: user?.user_id,
        product_id: image?.public_id,
        meta_event_id: data.meta_event_id || "",
        metadata: {
          dish_name: image?.dish_name || "",
          download_number: downloadNum,
          is_free_user: isFreeUser,
          preview_opened: true,
        },
      });

      // Update user state to reflect new download count
      if (onUserUpdate) onUserUpdate();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      
      // Handle specific abuse prevention errors
      if (status === 403 && data?.code === "FREE_LIMIT_REACHED") {
        showPricingAndClose();
        return;
      }
      
      if (status === 423) {
        // Account locked or device limit
        toast.error(data?.message || "Your account is temporarily locked. Please contact support.");
        return;
      }
      
      const msg = data?.detail || data?.message || "Download failed";
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const blockRightClick = (e) => e.preventDefault();

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center"
        onContextMenu={blockRightClick}
        data-testid="preview-modal"
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          data-testid="preview-backdrop"
        />

        <div className="relative z-10 flex flex-col items-center max-w-[90vw] max-h-[90vh]">
          {/* Top bar */}
          <div className="w-full flex items-start justify-between gap-3 mb-3 px-1">
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <h3
                className="text-white text-sm md:text-base font-medium whitespace-normal break-words leading-snug"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="preview-dish-name"
              >
                {image.dish_name}
              </h3>
              {images.length > 1 && currentIndex >= 0 && (
                <span className="text-white/50 text-xs shrink-0">
                  {currentIndex + 1} / {images.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Download counter badge — hidden for anonymous browsers */}
              {!isAnonymous && remaining > 0 && (
                <span
                  data-testid="download-counter-badge"
                  className={`hidden sm:inline-block text-xs font-medium px-2.5 py-1 rounded-full ${
                    remaining === 1
                      ? "bg-amber-500/20 text-amber-300 animate-pulse"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {isFree
                    ? (remaining === 1
                        ? "Last free sample download"
                        : `${remaining} free sample downloads remaining`)
                    : `${remaining} download${remaining !== 1 ? "s" : ""} remaining`}
                </span>
              )}
              {!isAnonymous && remaining === 0 && (
                <span
                  data-testid="download-counter-badge"
                  className="hidden sm:inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-red-500/20 text-red-300"
                >
                  {isFree ? "0 free sample downloads remaining" : "0 downloads remaining"}
                </span>
              )}
              <Button
                data-testid="preview-close-btn"
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-9 w-9 text-white/70 hover:text-white hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <div className="relative rounded-lg overflow-hidden bg-black/40">
            {!imgLoaded && (
              <div className="flex items-center justify-center" style={{ width: "60vw", height: "60vh" }}>
                <Loader2 className="h-10 w-10 animate-spin text-white/50" />
              </div>
            )}
            <canvas
              ref={canvasRef}
              className={`max-w-[85vw] max-h-[75vh] object-contain select-none ${imgLoaded ? "block" : "hidden"} ${isPremiumLocked ? "blur-lg" : ""}`}
              style={{ userSelect: "none", WebkitUserSelect: "none" }}
              draggable={false}
              data-testid="preview-canvas"
            />
            {/* Premium lock overlay in preview */}
            {isPremiumLocked && imgLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-10" data-testid="preview-premium-overlay">
                <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center mb-4 shadow-lg">
                  <Lock className="h-7 w-7 text-slate-700" />
                </div>
                <p className="text-base font-bold text-white drop-shadow-md mb-1">
                  {isStarter ? "Upgrade to unlock premium photos" : "Locked Premium Image"}
                </p>
                <p className="text-sm text-white/70 mb-1">
                  {isStarter
                    ? "Starter gives downloads. Growth unlocks full library"
                    : "Unlock instantly to access this and thousands more menu-ready photos."}
                </p>
                {!isStarter && <p className="text-[11px] text-white/50 mb-5">Pre-approved for Zomato & Swiggy</p>}
                {isStarter && <div className="mb-5" />}
                <Button
                  onClick={() => {
                    if (isAnonymous) {
                      onAnonymousDownload?.(image);
                    } else {
                      showPricingAndClose();
                    }
                  }}
                  className="h-11 px-6 text-sm font-semibold rounded-xl bg-white text-[#18181B] hover:bg-white/90 shadow-lg"
                  data-testid="preview-unlock-btn"
                >
                  <Sparkles className="h-4 w-4 mr-2 text-orange-500" />
                  {isAnonymous ? "Download Free Sample" : (isStarter ? "Upgrade Plan" : "Unlock Studio Pass 2.0")}
                </Button>
              </div>
            )}

            {/* Floating Download button - bottom-center of image */}
            {!isPremiumLocked && imgLoaded && (
              <Button
                data-testid="preview-download-btn"
                onClick={handleDownload}
                disabled={downloading}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-white text-[#09090B] hover:bg-white/90 font-medium text-sm h-11 px-6 rounded-full shadow-xl backdrop-blur-sm"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    {isAnonymous ? "Download Free Sample" : "Download"}
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Mobile-only counter badge below image — hidden for anonymous browsers */}
          {!isAnonymous && (remaining > 0 || remaining === 0) && (
            <div className="sm:hidden mt-2">
              <span
                data-testid="download-counter-badge-mobile"
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  remaining === 0
                    ? "bg-red-500/20 text-red-300"
                    : remaining === 1
                    ? "bg-amber-500/20 text-amber-300 animate-pulse"
                    : "bg-white/10 text-white/70"
                }`}
              >
                {isFree
                  ? (remaining === 0
                      ? "0 free sample downloads remaining"
                      : remaining === 1
                      ? "Last free sample download"
                      : `${remaining} free sample downloads remaining`)
                  : (remaining === 0
                      ? "0 downloads remaining"
                      : `${remaining} download${remaining !== 1 ? "s" : ""} remaining`)}
              </span>
            </div>
          )}

          {/* Navigation */}
          {images.length > 1 && (
            <>
              {currentIndex > 0 && (
                <button
                  data-testid="preview-prev-btn"
                  onClick={() => onNavigate(images[currentIndex - 1])}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors z-30"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              {currentIndex < images.length - 1 && (
                <button
                  data-testid="preview-next-btn"
                  onClick={() => onNavigate(images[currentIndex + 1])}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors z-30"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Agency limit modal */}
      {showAgencyLimit && (
        <AgencyLimitModal
          daysUntilReset={agencyDaysReset}
          onUpgrade={() => {
            setShowAgencyLimit(false);
            toast.info("Contact us for Agency Plan access.");
          }}
          onClose={() => setShowAgencyLimit(false)}
        />
      )}
    </>
  );
}
